import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/ConfirmationModal';
import { useMessage } from 'components/ui';
import { type AtlasGqlTableView, useDeleteTableViewMutation } from 'types/atlas-graphql';

export type DeleteViewState = {
  isOpen: boolean;
  tableId?: string;
  tableView?: AtlasGqlTableView;
};

type DeleteViewModalProps = {
  deleteViewState: DeleteViewState;
  onClose: () => void;
  onSuccess?: (view: AtlasGqlTableView) => void;
  onError?: (error: Error) => void;
};

export const DeleteViewModal: React.FunctionComponent<DeleteViewModalProps> = ({
  deleteViewState,
  onClose,
  onSuccess,
  onError,
}) => {
  const message = useMessage();
  const { t } = useTranslation();
  const { isOpen, tableView } = deleteViewState;

  // create table view mutation
  const [deleteTableView, { loading: deleteTableViewLoading }] = useDeleteTableViewMutation({
    onCompleted: data => {
      const isTableViewDeleted = data.deleteTableView;
      if (!!tableView && isTableViewDeleted) {
        message.success(
          t('components.viewDeletedSuccess', '{{name}} deleted successfully', {
            name: tableView.name,
          })
        );
        onSuccess && onSuccess(tableView);
      }

      onClose();
    },
    onError: error => {
      message.error(
        t('components.viewDeleteError', 'There was an error deleting the view. Please try again.')
      );
      onError && onError(error);

      onClose();
    },
    update: (cache, { data }) => {
      const isTableViewDeleted = data?.deleteTableView;
      if (!!tableView && isTableViewDeleted) {
        const cacheId = cache.identify({
          id: tableView.id,
          __typename: 'TableView',
        });
        cache.evict({ id: cacheId });
      }
    },
  });

  // handle submit logic
  const handleSubmit = () => {
    !!tableView &&
      deleteTableView({
        variables: {
          id: tableView.id,
        },
      });
  };

  return (
    <ConfirmationModal
      open={isOpen && !!tableView}
      title={t('components.deleteView', 'Delete View')}
      okText={t('common.delete', 'Delete')}
      onOk={handleSubmit}
      cancelText={t('common.cancel', 'Cancel')}
      onCancel={onClose}
      message={t(
        'components.deleteViewConfirm',
        'This action will delete {{name}}. Do you wish to continue?',
        { name: tableView?.name }
      )}
      loading={deleteTableViewLoading}
      destroyOnClose
    />
  );
};
